import type { Dispatch, SetStateAction } from "react";
import type { ClampedRegion } from "./region";
import { apiRequest } from "../../lib/api";
import { mediaUploadQuery } from "../../lib/endpoints";
import { uploadWithProgress } from "../../lib/upload";

/**
 * The player's own profile, as GET /api/p/:token/me and PATCH /api/p/:token return it.
 * Name and number are read-only for the player.
 */
export interface PlayerProfile {
  readonly name: string;
  readonly number: number | null;
  readonly photoMediaId: string | null;
  readonly photoLocked: boolean;
  readonly songLocked: boolean;
  readonly songMediaId: string | null;
  readonly songLabel: string | null;
  readonly startMs: number;
  readonly lengthS: number;
  readonly locked: boolean;
}

/** Fields a player may change. Each key is optional; the server applies only what is sent. */
export interface PlayerPatch {
  readonly song_media_id?: string;
  readonly start_ms?: number;
  readonly length_s?: number;
  readonly photo_media_id?: string;
  readonly consent?: boolean;
}

export interface MediaRef {
  readonly id: string;
}

/** Upload response: the stored media row, and whether this request created it (false on a repeat). */
export interface MediaUploadResponse {
  readonly media: MediaRef;
  readonly created: boolean;
}

export type UploadKind = "photo" | "song";

/** Shared props for the photo and song panels on the player page. */
export interface PlayerPanelProps {
  readonly token: string;
  readonly profile: PlayerProfile;
  readonly onProfile: (next: PlayerProfile) => void;
  readonly onFailure: (error: unknown) => void;
  readonly onSuccess: (
    messageKey: "player.photoSaved" | "player.songSaved" | "player.regionSaved",
  ) => void;
}

export type SongPhase =
  { readonly kind: "prepare" } | { readonly kind: "upload"; readonly fraction: number };

export type SetSongPhase = Dispatch<SetStateAction<SongPhase | null>>;

export type { ClampedRegion };

function base(token: string): string {
  return `/api/p/${encodeURIComponent(token)}`;
}

export const playerApi = {
  me: (token: string) => apiRequest<PlayerProfile>(`${base(token)}/me`),

  patch: (token: string, patch: PlayerPatch) =>
    apiRequest<PlayerProfile>(base(token), { method: "PATCH", body: patch }),

  upload: (
    token: string,
    kind: UploadKind,
    file: Blob,
    ext: string,
    sha256: string,
    onProgress: (fraction: number) => void,
    durationMs?: number,
  ) =>
    uploadWithProgress<MediaUploadResponse>(
      `${base(token)}/media${mediaUploadQuery({ kind, ext, sha256, ...(durationMs !== undefined ? { durationMs } : {}) })}`,
      file,
      onProgress,
    ),

  mediaUrl: (token: string, mediaId: string) =>
    `${base(token)}/media/${encodeURIComponent(mediaId)}`,
};
